import { addDays, format, isAfter, parseISO, startOfDay } from "date-fns";
import type { DietDateManager } from "@/lib/helpers/diet-dates";
import type { ConfigurationItem, ProductItemInput } from "../types";

const DATE_FORMAT = "yyyy-MM-dd";
const SEARCH_DAYS_LIMIT = 365;

export type DateMove = { from: string; to: string };

export type DateMoves = Record<string, DateMove[]>;

const byDate = (a: string, b: string) => a.localeCompare(b);

export const findDateMoves = (
  dates: string[],
  manager: DietDateManager,
): DateMove[] | null => {
  const unavailable = dates
    .filter((date) => !manager.areDatesAvailable([parseISO(date)]))
    .sort(byDate);

  if (unavailable.length === 0) {
    return [];
  }

  const taken = new Set(dates);
  const lastDay = startOfDay(
    manager.lastPossibleOrderDay ?? addDays(new Date(), SEARCH_DAYS_LIMIT),
  );
  const moves: DateMove[] = [];

  for (
    let day = startOfDay(new Date());
    moves.length < unavailable.length && !isAfter(day, lastDay);
    day = addDays(day, 1)
  ) {
    const from = unavailable[moves.length];
    const to = format(day, DATE_FORMAT);
    if (from && !taken.has(to) && manager.areDatesAvailable([day])) {
      moves.push({ from, to });
    }
  }

  return moves.length === unavailable.length ? moves : null;
};

const moveDate = (date: string, moves: DateMove[]) =>
  moves.find((move) => move.from === date)?.to ?? date;

export const moveItemDates = (
  item: ConfigurationItem,
  moves?: DateMove[],
): ConfigurationItem => {
  if (!moves?.length) {
    return item;
  }

  return {
    ...item,
    dates: item.dates.map((date) => moveDate(date, moves)).sort(byDate),
    subItems: item.subItems?.map((subItem) => ({
      ...subItem,
      date: moveDate(subItem.date, moves),
    })),
  };
};

export const moveProductItemDate = (
  productItem: ProductItemInput,
  moves?: DateMove[],
): ProductItemInput =>
  moves?.length
    ? { ...productItem, date: moveDate(productItem.date, moves) }
    : productItem;
